import { getViagens, deleteViagem } from "@spark/services";
import { useState, useEffect, useCallback, useRef } from 'react';
import { notification } from '@spark/ui-components/utils/notification';
import { useLatestAsyncRequest } from '@spark/ui-components/hooks/useLatestAsyncRequest';
import { formatarValorBRL } from '@spark/utils/formatters';

export const useViagensListagem = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [paginationState, setPaginationState] = useState({
    pageIndex: 0,
    pageSize: 10
  });
  const didInitialLoadRef = useRef(false);
  const inFlightSignatureRef = useRef(null);
  const { beginRequest, isCurrentRequest, isMounted } = useLatestAsyncRequest();
  const [filtros, setFiltros] = useState({
    NomeMotorista: '',
    NumeroPlaca: '',
    NumeroFrota: '',
    NumeroCarga: '',
    CidadeOrigem: '',
    CidadeDestino: '',
    DataInicio: '',
    DataFim: '',
    IdMotorista: '',
    IdVeiculo: '',
    IdCarga: ''
  });

  // Carregar dados
  const fetchData = useCallback(async (filtrosParaBuscar = filtros) => {
    if (!isMounted()) return;
    let requestId;

    try {
      console.log('🌐 Buscando viagens com filtros:', filtrosParaBuscar);

      // Preparar filtros para o serviço
      const filters = {
        quantidadePorPagina: paginationState.pageSize,
        pagina: paginationState.pageIndex + 1,
        ...(filtrosParaBuscar.NomeMotorista && { NomeMotorista: filtrosParaBuscar.NomeMotorista }),
        ...(filtrosParaBuscar.NumeroPlaca && { NumeroPlaca: filtrosParaBuscar.NumeroPlaca }),
        ...(filtrosParaBuscar.NumeroFrota && { NumeroFrota: filtrosParaBuscar.NumeroFrota }),
        ...(filtrosParaBuscar.NumeroCarga && { NumeroCarga: filtrosParaBuscar.NumeroCarga }),
        ...(filtrosParaBuscar.CidadeOrigem && { CidadeOrigem: filtrosParaBuscar.CidadeOrigem }),
        ...(filtrosParaBuscar.CidadeDestino && { CidadeDestino: filtrosParaBuscar.CidadeDestino }),
        ...(filtrosParaBuscar.DataInicio && { DataInicio: filtrosParaBuscar.DataInicio }),
        ...(filtrosParaBuscar.DataFim && { DataFim: filtrosParaBuscar.DataFim }),
        ...(filtrosParaBuscar.IdMotorista && { IdMotorista: filtrosParaBuscar.IdMotorista }),
        ...(filtrosParaBuscar.IdVeiculo && { IdVeiculo: filtrosParaBuscar.IdVeiculo }),
        ...(filtrosParaBuscar.IdCarga && { IdCarga: filtrosParaBuscar.IdCarga })
      };

      // Evitar chamadas duplicadas (StrictMode/dev ou race de effects)
      const signature = JSON.stringify(filters);
      if (inFlightSignatureRef.current === signature) {
        console.log('⏭️ Pulando chamada duplicada (mesma assinatura)');
        return;
      }

      requestId = beginRequest();
      inFlightSignatureRef.current = signature;
      setLoading(true);
      const response = await getViagens(filters);
      if (!isCurrentRequest(requestId)) return;

      // A API retorna { data: { registros, totalRegistros, ... } } ou pode retornar direto
      const responseData = response?.data?.data || response;

      console.log('📥 Resposta da API:', responseData);
      console.log('📦 Registros:', responseData.registros);
      console.log('📦 Total de registros:', responseData.totalRegistros);
      console.log('📦 Página atual:', responseData.paginaAtual);

      const dadosProcessados = (responseData.registros || []).map(item => ({
        id: item.Id,
        codigoIntegracao: item.CodigoIntegracao || '',
        nomeMotorista: item.NomeMotorista || '',
        idMotorista: item.IdMotorista,
        numeroPlaca: item.NumeroPlaca || '',
        numeroFrota: item.NumeroFrota || '',
        idVeiculo: item.IdVeiculo,
        numeroCarga: item.NumeroCarga || '',
        idCarga: item.IdCarga,
        cidadeOrigem: item.NomeCidadeOrigem || '',
        cidadeDestino: item.NomeCidadeDestino || '',
        ufOrigem: item.SiglaEstadoOrigem || '',
        ufDestino: item.SiglaEstadoDestino || '',
        dataInicio: item.DataInicio ? new Date(item.DataInicio).toLocaleDateString('pt-BR') : '',
        dataFim: item.DataFim ? new Date(item.DataFim).toLocaleDateString('pt-BR') : '',
        totalKmRodado: item.TotalKmRodado ? `${parseFloat(item.TotalKmRodado).toFixed(2)} km` : '0 km',
        valorFreteLiquido: item.ValorFreteLiquido || 0,
        valorFreteBruto: item.ValorFreteBruto || 0,
        notasFiscais: item.NotasFiscais || '',
        dataCriacao: item.DataCriacao ? new Date(item.DataCriacao).toLocaleDateString('pt-BR') : ''
      }));

      console.log('✅ Dados processados:', dadosProcessados);
      setData(dadosProcessados);
      setTotalRegistros(responseData.totalRegistros || dadosProcessados.length);
    } catch (error) {
      if (requestId && !isCurrentRequest(requestId)) return;
      if (!isMounted()) return;
      console.error('❌ Erro ao buscar dados:', error);
      // Erro tratado automaticamente pelo interceptor global do axios
    } finally {
      if (requestId && isCurrentRequest(requestId)) {
        setLoading(false);
        setFilterLoading(false);
        inFlightSignatureRef.current = null;
      }
    }
  }, [beginRequest, filtros, isCurrentRequest, isMounted, paginationState.pageIndex, paginationState.pageSize]);

  // Disparar fetch ao alterar paginação pelo setPaginationState
  useEffect(() => {
    if (!didInitialLoadRef.current) return;
    fetchData(filtros);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationState.pageIndex, paginationState.pageSize]);

  // Carregar dados iniciais
  useEffect(() => {
    if (didInitialLoadRef.current) return; // evita dupla chamada em StrictMode
    didInitialLoadRef.current = true;
    setLoading(true);
    fetchData();
  }, []);

  // Log quando dados mudam
  useEffect(() => {
    console.log('📊 Dados atualizados:', data);
    console.log('📊 Total de registros:', totalRegistros);
    console.log('📊 Loading:', loading);
  }, [data, totalRegistros, loading]);

  // Aplicar filtros
  const handleFilterApply = useCallback((newFilters) => {
    console.log('🔍 Aplicando filtros:', newFilters);
    setFiltros(newFilters);
    setFilterLoading(true);
    setPaginationState(prev => ({ ...prev, pageIndex: 0 }));
    fetchData(newFilters);
  }, [fetchData]);

  // Limpar filtros
  const handleLimparFiltros = useCallback((clearedFiltros) => {
    console.log('🧹 Limpar filtros clicado. Filtros limpos:', clearedFiltros);
    setFiltros(clearedFiltros);
    setLoading(true);
    setPaginationState(prev => ({ ...prev, pageIndex: 0 }));
    fetchData(clearedFiltros);
  }, [fetchData]);

  // Paginação: garantir skeleton imediato ao alterar página/tamanho
  const handlePaginationChange = useCallback((next) => {
    setLoading(true);
    setPaginationState(prev => {
      if (typeof next === 'function') return next(prev);
      if (typeof next === 'number') return { ...prev, pageIndex: next };
      if (next && typeof next === 'object') return { ...prev, ...next };
      return prev;
    });
  }, []);

  // Excluir viagem
  const handleExcluir = useCallback(async (id) => {
    console.log('🗑️ Excluindo viagem:', id);
    try {
      await deleteViagem(id);
      notification.success('Viagem excluída com sucesso!');
      fetchData(); // Recarregar dados
    } catch (error) {
      console.error('❌ Erro ao excluir viagem:', error);
      // Erro tratado automaticamente pelo interceptor global do axios
    }
  }, [fetchData]);

  return {
    // Estado
    data,
    loading,
    filterLoading,
    totalRegistros,
    filtros,
    paginationState,
    // Handlers
    handleFilterApply,
    handleLimparFiltros,
    handleExcluir,
    handlePaginationChange,
    setPaginationState,
    // Funções de carregamento
    fetchData
  };
};
